import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Send, Plus, Pencil, Trash2, Copy, ArrowLeft, Users,
  MessageSquare, Mail, Phone, Mic, Share2,
  Clock, Calendar, CheckCircle, X, Save,
  Globe, ClipboardList, GitBranch, FileText
} from 'lucide-react';
import { BulkMessageComposer, BulkMessageComposerRef } from './BulkMessageComposer';
import { SocialMediaComposer } from './SocialMediaComposer';
import { BulkMailCampaignPage } from './BulkMailCampaignPage';
// CampaignMemberSend not used here — engagement uses its own member send
import { campaignsApi, Engagement, CreateEngagementRequest, chatflowsApi } from '@/services/campaignsApi';
import { groupsApi } from '@/services/groupsApi';
import { surveysApi } from '@/services/surveysApi';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { VariablePicker, TemplatePreviewButton, insertToken } from '@/components/messaging/templateVariables';
import { useComplianceMode } from '@/hooks/useComplianceMode';
import { Shield } from 'lucide-react';
import { ComplianceCheckPanel } from '@/components/compliance/ComplianceCheckPanel';
import type { ValidateSendResult } from '@/services/api/communicationValidate';
import { OptimalSendTimeBadge } from '@/components/communications/ml';

type ChannelType = 'sms' | 'email' | 'whatsapp' | 'voice' | 'social' | 'survey' | 'chatflow' | 'mail';
type StatusType = 'draft' | 'active' | 'sent' | 'scheduled';
type ViewType = 'list' | 'editor';

interface FormData {
  name: string;
  description: string;
  language: string;
  channel: ChannelType;
  status: 'draft' | 'active';
  schedule_enabled: boolean;
  scheduled_at: string;
  wait_for_response: boolean;
  timeout_hours: number;
  survey_id: string;
  chatflow_id: string;
}

const LANGUAGES = [
  'English', 'Spanish', 'Chinese', 'Vietnamese', 'Korean',
  'Tagalog', 'Arabic', 'French', 'Portuguese', 'Haitian Creole', 'Russian',
];

const CHANNELS: { key: ChannelType; label: string; icon: React.ReactNode; color: string }[] = [
  { key: 'sms', label: 'SMS', icon: <MessageSquare className="w-4 h-4" />, color: 'blue' },
  { key: 'email', label: 'Email', icon: <Mail className="w-4 h-4" />, color: 'purple' },
  { key: 'whatsapp', label: 'WhatsApp', icon: <Phone className="w-4 h-4" />, color: 'green' },
  { key: 'voice', label: 'Voice AI', icon: <Mic className="w-4 h-4" />, color: 'indigo' },
  { key: 'social', label: 'Social Media', icon: <Share2 className="w-4 h-4" />, color: 'pink' },
  { key: 'survey', label: 'Survey', icon: <ClipboardList className="w-4 h-4" />, color: 'amber' },
  { key: 'chatflow', label: 'Chatflow', icon: <GitBranch className="w-4 h-4" />, color: 'teal' },
  { key: 'mail', label: 'Mail / Letters', icon: <FileText className="w-4 h-4" />, color: 'orange' },
];

const STATUS_BADGES: Record<StatusType, { bg: string; text: string; label: string }> = {
  draft: { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', label: 'Draft' },
  active: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Active' },
  sent: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'Sent' },
  scheduled: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', label: 'Scheduled' },
};

const DEFAULT_FORM: FormData = {
  name: '',
  description: '',
  language: 'English',
  channel: 'email',
  status: 'draft',
  schedule_enabled: false,
  scheduled_at: '',
  wait_for_response: false,
  timeout_hours: 24,
  survey_id: '',
  chatflow_id: '',
};

// Inline member send component for engagements (uses engagement endpoints, not campaign)
const EngagementMemberSendInline: React.FC<{ engagementId: string; groupId: string; onClose: () => void }> = ({ engagementId, groupId, onClose }) => {
  const [members, setMembers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [channel, setChannel] = useState<'sms' | 'email'>('sms');
  const [sending, setSending] = useState<Record<number, 'sending' | 'sent' | 'failed'>>({});
  const [search, setSearch] = useState('');

  useEffect(() => {
    campaignsApi.getEngagementGroupMembers(engagementId, groupId)
      .then(data => setMembers(data.members || []))
      .catch(err => console.error('Failed to load members', err))
      .finally(() => setLoading(false));
  }, [engagementId, groupId]);

  const sendToMember = async (memberId: number) => {
    setSending(prev => ({ ...prev, [memberId]: 'sending' }));
    try {
      await campaignsApi.sendEngagementIndividual(engagementId, groupId, memberId, channel);
      setSending(prev => ({ ...prev, [memberId]: 'sent' }));
    } catch {
      setSending(prev => ({ ...prev, [memberId]: 'failed' }));
    }
  };

  const filtered = members.filter(m => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (m.first_name || '').toLowerCase().includes(q) || (m.last_name || '').toLowerCase().includes(q);
  });

  const sentCount = Object.values(sending).filter(s => s === 'sent').length;
  const failedCount = Object.values(sending).filter(s => s === 'failed').length;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Send to Members Individually</h2>
          <p className="text-sm text-gray-500">{members.length} members | {sentCount} sent | {failedCount} failed</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex border rounded-lg overflow-hidden">
            <button onClick={() => setChannel('sms')} className={`px-3 py-1.5 text-sm ${channel === 'sms' ? 'bg-v2accent text-white' : 'bg-surface text-ink-500 hover:text-ink'}`}>SMS</button>
            <button onClick={() => setChannel('email')} className={`px-3 py-1.5 text-sm ${channel === 'email' ? 'bg-v2accent text-white' : 'bg-surface text-ink-500 hover:text-ink'}`}>Email</button>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
      </div>
      <input
        type="text" placeholder="Search members..." value={search} onChange={e => setSearch(e.target.value)}
        className="w-full mb-4 px-3 py-2 border rounded-lg text-sm"
      />
      {loading ? (
        <p className="text-center text-gray-400 py-8">Loading members...</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-gray-50">
            <th className="text-left px-4 py-2 font-medium text-gray-500">Name</th>
            <th className="text-left px-4 py-2 font-medium text-gray-500">Phone</th>
            <th className="text-left px-4 py-2 font-medium text-gray-500">Email</th>
            <th className="text-right px-4 py-2 font-medium text-gray-500">Action</th>
          </tr></thead>
          <tbody className="divide-y">
            {filtered.map(m => (
              <tr key={m.member_id} className="hover:bg-gray-50">
                <td className="px-4 py-2 font-medium">{m.first_name} {m.last_name}</td>
                <td className="px-4 py-2 text-gray-500">{m.phone || '—'}</td>
                <td className="px-4 py-2 text-gray-500">{m.email || '—'}</td>
                <td className="px-4 py-2 text-right">
                  {sending[m.member_id] === 'sent' ? (
                    <span className="text-green-600 text-xs flex items-center justify-end gap-1"><CheckCircle className="w-3 h-3" /> Sent</span>
                  ) : sending[m.member_id] === 'failed' ? (
                    <button onClick={() => sendToMember(m.member_id)} className="text-red-600 text-xs flex items-center justify-end gap-1">Failed — Retry</button>
                  ) : sending[m.member_id] === 'sending' ? (
                    <span className="text-gray-400 text-xs">Sending...</span>
                  ) : (
                    <button onClick={() => sendToMember(m.member_id)} className="text-xs px-3 py-1 bg-v2accent text-white rounded hover:bg-v2accent/90">Send</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

interface BulkCommunicationsPageProps {
  initialGroupId?: string;
}

export const BulkCommunicationsPage: React.FC<BulkCommunicationsPageProps> = ({ initialGroupId }) => {
  const { t } = useAppTranslation();
  const { isRegulated } = useComplianceMode();
  const [view, setView] = useState<ViewType>('list');
  const [engagements, setEngagements] = useState<Engagement[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({ ...DEFAULT_FORM });
  const [surveys, setSurveys] = useState<{ id: string; name: string }[]>([]);
  // Survey invitation text. {{survey_link}} is filled per member at send time.
  const [surveyMessage, setSurveyMessage] = useState(
    "Hi {{first_name}}, we'd love your feedback. Please complete this quick survey: {{survey_link}}",
  );
  const surveyMessageRef = useRef<HTMLTextAreaElement | null>(null);
  const [chatflows, setChatflows] = useState<{ id: string; name: string }[]>([]);
  const composerRef = useRef<BulkMessageComposerRef>(null);
  const [channelFilter, setChannelFilter] = useState<'all' | ChannelType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | StatusType>('all');
  const [showIndividualSend, setShowIndividualSend] = useState(false);
  const [individualSendGroupId, setIndividualSendGroupId] = useState('');
  const [groups, setGroups] = useState<{ id: string; name: string; member_count: number }[]>([]);
  // Compliance gating — live state from the ComplianceCheckPanel
  const [composerAssetIds, setComposerAssetIds] = useState<number[]>([]);
  const [composerTemplateId, setComposerTemplateId] = useState<number | null>(null);
  const [composerGroupId, setComposerGroupId] = useState<string>('');
  const [groupMemberSample, setGroupMemberSample] = useState<number[]>([]);
  const [complianceGate, setComplianceGate] = useState<{ ok: boolean; result: ValidateSendResult | null }>({
    ok: true, result: null,
  });
  // When the panel reports a hard block, Send Now is disabled until override.
  const sendBlockedByCompliance = isRegulated && !complianceGate.ok;

  // When the composer's groupId changes, fetch a sample of members so the
  // ComplianceCheckPanel can run the per-recipient consent gate. We only
  // need a representative sample (validate-send's sample_only flag caps it
  // server-side too), so 25 is plenty.
  useEffect(() => {
    if (!composerGroupId || !isRegulated) {
      setGroupMemberSample([]);
      return;
    }
    let mounted = true;
    groupsApi
      .getGroupMembers(composerGroupId, { limit: 25 })
      .then((data: any) => {
        if (!mounted) return;
        const list = Array.isArray(data) ? data : data?.members || [];
        const ids = list
          .map((m: any) => {
            const v = m.member_id ?? m.id;
            const n = typeof v === 'number' ? v : Number(v);
            return Number.isFinite(n) ? n : null;
          })
          .filter((x: number | null): x is number => x !== null);
        setGroupMemberSample(ids);
      })
      .catch(() => mounted && setGroupMemberSample([]));
    return () => {
      mounted = false;
    };
  }, [composerGroupId, isRegulated]);

  const fetchEngagements = useCallback(async () => {
    setLoading(true);
    try {
      const data = await campaignsApi.listEngagements();
      setEngagements(data);
    } catch (err) {
      console.error('Failed to load engagements', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEngagements(); }, [fetchEngagements]);

  const openEditor = (engagement?: Engagement) => {
    if (engagement) {
      setEditingId(engagement.id);
      // Preserve real backend status (sent/cancelled/completed). Map only
      // unknown/legacy values back to 'draft' for the form-state typing.
      const rawStatus = engagement.status as string;
      const engStatus = (['draft', 'active', 'sent', 'cancelled', 'completed'].includes(rawStatus)
        ? rawStatus
        : 'draft') as 'draft' | 'active' | 'sent' | 'cancelled' | 'completed';
      const hasSchedule = !!engagement.scheduled_at;
      // Legacy seed rows used engagement_type='message' for any text-channel
      // engagement. Infer the real channel from the name suffix or content
      // so the editor renders the right composer (was blank-page for these).
      const rawType = (engagement.engagement_type || 'sms') as string;
      const inferredChannel: ChannelType = (
        ['sms', 'email', 'whatsapp', 'voice', 'social', 'survey', 'chatflow', 'mail'].includes(rawType)
          ? rawType
          : /email/i.test(engagement.name || '')
            ? 'email'
            : /voice|ivr/i.test(engagement.name || '')
              ? 'chatflow'
              : /survey|poll/i.test(engagement.name || '')
                ? 'survey'
                : engagement.email_subject
                  ? 'email'
                  : 'sms'
      ) as ChannelType;
      setFormData({
        name: engagement.name,
        description: engagement.description || '',
        language: 'English',
        channel: inferredChannel,
        status: engStatus,
        schedule_enabled: hasSchedule,
        scheduled_at: engagement.scheduled_at || '',
        wait_for_response: engagement.wait_for_response ?? false,
        timeout_hours: engagement.timeout_hours ?? 24,
        survey_id: engagement.survey_id || '',
        chatflow_id: engagement.chatflow_id || '',
      });
    } else {
      setEditingId(null);
      setFormData({ ...DEFAULT_FORM });
    }
    setView('editor');
  };

  const handleCopy = async (engagement: Engagement) => {
    try {
      await campaignsApi.createEngagement({
        name: `${engagement.name} (Copy)`,
        description: engagement.description,
        engagement_type: engagement.engagement_type,
        content_template: engagement.content_template,
        survey_id: engagement.survey_id,
        chatflow_id: engagement.chatflow_id,
        wait_for_response: engagement.wait_for_response,
        timeout_hours: engagement.timeout_hours,
        organization_id: engagement.organization_id,
      });
      fetchEngagements();
    } catch (err) {
      console.error('Failed to copy engagement', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this engagement?')) return;
    try {
      await campaignsApi.deleteEngagement(id);
      fetchEngagements();
    } catch (err) {
      console.error('Failed to delete engagement', err);
    }
  };

  const handleSave = async (overrideStatus?: 'draft' | 'active', stayOnEditor?: boolean): Promise<string | null> => {
    // Required-field guard: name is mandatory at every save (draft or active).
    // Save buttons used to silently substitute "Untitled Engagement" — that
    // hid the missing field from the user.
    if (!formData.name || !formData.name.trim()) {
      alert('Engagement name is required. Please enter a name before saving.');
      const nameInput = document.getElementById('engagement-name-input') as HTMLInputElement | null;
      nameInput?.focus();
      return null;
    }

    const engagementType = (['sms', 'email', 'survey', 'chatflow'].includes(formData.channel)
      ? formData.channel
      : 'sms') as CreateEngagementRequest['engagement_type'];

    const orgId = Number(localStorage.getItem('active_cbo_id')) || 1;

    const effectiveStatus = overrideStatus || formData.status;

    // Read actual message content from the composer
    let contentTemplate = '';
    let emailSubject = '';
    if (composerRef.current) {
      const composerContent = composerRef.current.getContent();
      contentTemplate = composerContent.message || composerContent.htmlContent || composerContent.voiceScript || '';
      emailSubject = composerContent.subject || '';
    }
    if (!contentTemplate) {
      contentTemplate = formData.channel === 'survey' ? 'Survey engagement'
        : formData.channel === 'chatflow' ? 'Chatflow engagement'
        : '';
    }
    // Prepend email subject if present
    if (emailSubject && formData.channel === 'email') {
      contentTemplate = `Subject: ${emailSubject}\n\n${contentTemplate}`;
    }

    // Composer state — group + attached assets + email subject — so the
    // engagement rehydrates fully on next edit instead of forcing the user
    // to rebuild it.
    const persistedGroupId = composerGroupId || null;
    const persistedAssetIds = composerAssetIds || [];
    const persistedEmailSubject = emailSubject || null;

    try {
      let savedId = editingId;
      if (editingId) {
        // Update — can pass status and scheduled_at
        await campaignsApi.updateEngagement(editingId, {
          name: formData.name.trim(),
          description: formData.description || undefined,
          engagement_type: engagementType,
          content_template: contentTemplate,
          survey_id: formData.survey_id || undefined,
          chatflow_id: formData.chatflow_id || undefined,
          wait_for_response: formData.wait_for_response,
          timeout_hours: formData.timeout_hours,
          status: effectiveStatus,
          scheduled_at: formData.schedule_enabled ? formData.scheduled_at : undefined,
          group_id: persistedGroupId,
          asset_ids: persistedAssetIds,
          email_subject: persistedEmailSubject,
        });
      } else {
        // Create
        const created = await campaignsApi.createEngagement({
          name: formData.name.trim(),
          description: formData.description || undefined,
          organization_id: orgId,
          engagement_type: engagementType,
          content_template: contentTemplate,
          survey_id: formData.survey_id || undefined,
          chatflow_id: formData.chatflow_id || undefined,
          wait_for_response: formData.wait_for_response,
          timeout_hours: formData.timeout_hours,
          group_id: persistedGroupId,
          asset_ids: persistedAssetIds,
          email_subject: persistedEmailSubject,
        });
        savedId = created.id;
        setEditingId(savedId);
      }
      await fetchEngagements();
      if (!stayOnEditor) {
        setView('list');
      }
      return savedId;
    } catch (err) {
      console.error('Failed to save engagement', err);
      return null;
    }
  };

  // Lazy-load surveys, chatflows, and groups when opening editor
  useEffect(() => {
    if (view === 'editor') {
      groupsApi.listGroups().then((g: any) => {
        const list = Array.isArray(g) ? g : g?.groups || [];
        setGroups(list.map((x: any) => ({ id: x.id, name: x.name, member_count: x.member_count || 0 })));
      }).catch(() => {});
      // Load surveys from BOTH systems
      Promise.all([
        surveysApi.listSurveys().catch(() => []),
        import('@/services/apiClient').then(({ apiClient: api }) =>
          api.get('/api/interactive-content', { params: { content_type: 'survey' } }).then(r => r.data?.items || [])
        ).catch(() => []),
      ]).then(([surveyData, icData]) => {
        const combined: { id: string; name: string }[] = [];
        const sList = Array.isArray(surveyData) ? surveyData : (surveyData as any)?.surveys || [];
        sList.forEach((s: any) => combined.push({ id: s.id, name: s.name }));
        if (Array.isArray(icData)) {
          icData.forEach((ic: any) => combined.push({ id: ic.uuid || String(ic.id), name: `${ic.name} (Surveys & Polls)` }));
        }
        setSurveys(combined);
      });
      chatflowsApi.listChatflows().then((raw: any) => {
        const list = Array.isArray(raw) ? raw : raw?.chatflows || [];
        setChatflows(list.map((x: any) => ({ id: x.id, name: x.name })));
      }).catch(() => {});
    }
  }, [view]);

  const filteredEngagements = engagements.filter(e => {
    if (channelFilter !== 'all' && e.engagement_type !== channelFilter) return false;
    return true;
  });

  const getEngagementStatus = (e: Engagement): StatusType => {
    // Terminal states win over scheduled_at (legacy sends have scheduled_at
    // set to the actual send timestamp, which is in the past, not a schedule).
    if (e.status === 'sent') return 'sent';
    if (e.status === 'cancelled' || e.status === 'completed') return 'sent';
    if (e.scheduled_at && new Date(e.scheduled_at) > new Date()) return 'scheduled';
    if (e.status === 'active') return 'active';
    return 'draft';
  };

  const updateForm = (patch: Partial<FormData>) => setFormData(prev => ({ ...prev, ...patch }));

  /* ───────────────────────── LIST VIEW ───────────────────────── */
  if (view === 'list') {
    return (
      <div className="h-full flex flex-col bg-white dark:bg-gray-900">
        {/* Toolbar */}
        <div className="border-b border-v2border px-6 py-3 flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={() => openEditor()}
            className="flex items-center gap-2 px-4 py-2 bg-v2accent text-white rounded-lg hover:bg-v2accent/90 transition-colors font-medium text-sm"
          >
            <Plus className="w-4 h-4" />
            Create Engagement
          </button>
        </div>

        {/* Filter bar */}
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-4 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Channel</span>
            <select
              value={channelFilter}
              onChange={e => setChannelFilter(e.target.value as any)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <option value="all">All Channels</option>
              <option value="sms">SMS</option>
              <option value="email">Email</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="voice">Voice</option>
              <option value="social">Social</option>
              <option value="survey">Survey</option>
              <option value="chatflow">Chatflow</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</span>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as any)}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              <option value="all">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="sent">Sent</option>
              <option value="scheduled">Scheduled</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>
          ) : filteredEngagements.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Send className="w-12 h-12 mb-3 opacity-30" />
              <p className="text-lg font-medium">No engagements yet</p>
              <p className="text-sm mt-1">Create your first engagement to get started</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30">
                  <th className="text-left px-6 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase text-xs tracking-wider">Name</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase text-xs tracking-wider">Channel</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase text-xs tracking-wider">Status</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase text-xs tracking-wider">Language</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase text-xs tracking-wider">Audience</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase text-xs tracking-wider">Delivery</th>
                  <th className="text-left px-6 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase text-xs tracking-wider">Scheduled</th>
                  <th className="text-right px-6 py-3 font-medium text-gray-500 dark:text-gray-400 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {filteredEngagements.map(eng => {
                  const status = getEngagementStatus(eng);
                  const badge = STATUS_BADGES[status];
                  return (
                    <tr
                      key={eng.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors cursor-pointer"
                      onClick={(ev) => {
                        if ((ev.target as HTMLElement).closest('button')) return;
                        openEditor(eng);
                      }}
                    >
                      <td className="px-6 py-4">
                        <p className="font-medium text-gray-900 dark:text-gray-100">{eng.name}</p>
                        {eng.description && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate max-w-xs">{eng.description}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center gap-1.5 text-gray-700 dark:text-gray-300 capitalize">
                          {CHANNELS.find(c => c.key === eng.engagement_type)?.icon}
                          {eng.engagement_type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{(eng as any).language || 'English'}</td>
                      <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-xs whitespace-nowrap">
                        {(eng as any).audience_size > 0
                          ? <span className="font-medium text-gray-900 dark:text-gray-100">{(eng as any).audience_size}</span>
                          : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-6 py-4 text-xs whitespace-nowrap">
                        {(() => {
                          const d = (eng as any).delivered_count || 0;
                          const f = (eng as any).failed_count || 0;
                          const s = (eng as any).sent_count || 0;
                          const total = d + f + s;
                          if (total === 0) return <span className="text-gray-400">—</span>;
                          return (
                            <span className="inline-flex gap-2">
                              {d > 0 && <span className="text-green-700 dark:text-green-400" title="Delivered">✓ {d}</span>}
                              {f > 0 && <span className="text-red-600 dark:text-red-400" title="Failed">✗ {f}</span>}
                              {s > 0 && <span className="text-gray-500" title="Sent (no delivery confirmation)">→ {s}</span>}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                        {eng.scheduled_at
                          ? new Date(eng.scheduled_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => openEditor(eng)}
                            className="p-1.5 text-ink-500 hover:text-ink hover:bg-paper-2 rounded transition-colors"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleCopy(eng)}
                            className="p-1.5 text-ink-500 hover:text-ink hover:bg-paper-2 rounded transition-colors"
                            title="Copy"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(eng.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  /* ───────────────────────── EDITOR VIEW ───────────────────────── */
  const isMessagingChannel = ['sms', 'email', 'whatsapp', 'voice'].includes(formData.channel);

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Top bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setView('list')}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Engagements
          </button>
          <div className="h-5 w-px bg-gray-300 dark:bg-gray-600" />
          <span className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
            {formData.name || 'Untitled Engagement'}
          </span>
          {isRegulated && (
            <span
              className="inline-flex items-center gap-1 ml-3 px-2 py-1 text-xs font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border border-amber-200 dark:border-amber-800 rounded"
              title="This org runs in regulated mode. Templates must be approved and assets must be unexpired before sending."
            >
              <Shield className="w-3 h-3" />
              Compliance enforced at send
            </span>
          )}
          {(['sent', 'cancelled', 'completed'] as string[]).includes(formData.status as string) && (
            <span
              className="inline-flex items-center gap-1 ml-3 px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded"
              title="This engagement is finalized. Use Copy to create a new editable draft."
            >
              Read-only · {String(formData.status).toUpperCase()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSave('draft')}
            disabled={(['sent', 'cancelled', 'completed'] as string[]).includes(formData.status as string)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          >
            <Save className="w-4 h-4" />
            Save Draft
          </button>
          <button
            onClick={() => {
              if (!formData.schedule_enabled || !formData.scheduled_at) {
                // Toggle schedule panel on and scroll to it
                updateForm({ schedule_enabled: true });
                alert('Set a schedule date/time in the right panel, then click Schedule again to save.');
                return;
              }
              // Schedule is set — save with schedule
              handleSave('active');
            }}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
          >
            <Calendar className="w-4 h-4" />
            {formData.schedule_enabled && formData.scheduled_at ? 'Save & Schedule' : 'Schedule'}
          </button>
          <button
            onClick={async () => {
              if (sendBlockedByCompliance) {
                alert(
                  'This send is blocked by compliance. Resolve the issues in the Compliance Check panel above (or have an admin record an override) before sending.',
                );
                return;
              }
              // Save engagement as active first
              await handleSave('active', true);
              // Then trigger the actual bulk send via composer
              if (composerRef.current) {
                composerRef.current.triggerSend();
              }
            }}
            disabled={sendBlockedByCompliance}
            title={
              sendBlockedByCompliance
                ? 'Compliance check failed. See the panel above the composer.'
                : 'Send the engagement now'
            }
            className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg transition-colors font-medium ${
              sendBlockedByCompliance
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-v2accent text-white hover:bg-v2accent/90'
            }`}
          >
            <Send className="w-4 h-4" />
            Send Now
          </button>
          <button
            onClick={async () => {
              if (!formData.name) {
                alert('Please enter an engagement name before sending individually.');
                return;
              }
              if (sendBlockedByCompliance) {
                alert(
                  'This send is blocked by compliance. Resolve the issues in the Compliance Check panel above (or have an admin record an override) before sending.',
                );
                return;
              }
              // Always save content first so DB has latest message
              const savedId = await handleSave('active', true);
              if (!savedId) {
                alert('Failed to save engagement. Please try again.');
                return;
              }
              setShowIndividualSend(true);
            }}
            disabled={sendBlockedByCompliance}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg transition-colors ${
              sendBlockedByCompliance
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'border border-v2border bg-surface text-ink hover:bg-paper-2'
            }`}
          >
            <Users className="w-4 h-4" />
            Send Individual
          </button>
        </div>
      </div>

      {/* Channel selector tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6">
        <div className="flex items-center gap-1 -mb-px">
          {CHANNELS.map(ch => {
            const active = formData.channel === ch.key;
            return (
              <button
                key={ch.key}
                onClick={() => updateForm({ channel: ch.key })}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:border-gray-300'
                }`}
              >
                {ch.icon}
                {ch.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Compliance notice for regulated orgs */}
      {isRegulated && (
        <div className="px-6 pt-4">
          <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
            <Shield className="w-4 h-4 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-medium text-amber-900 dark:text-amber-200">
                Regulated entity — compliance gating active
              </div>
              <div className="text-xs text-amber-800 dark:text-amber-300 mt-1">
                When you click Send, this engagement is checked against approved templates,
                unexpired media, and per-recipient consent. Blocked sends require an admin
                override with reason.{' '}
                Manage policies in <strong>Org Settings → Compliance</strong>; review pending
                approvals in the Approval Queue.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Live compliance check — visible while composing in regulated mode */}
      {isRegulated && (
        <div className="px-6 pt-3">
          <ComplianceCheckPanel
            templateId={composerTemplateId ?? undefined}
            assetIds={composerAssetIds}
            memberIds={groupMemberSample}
            channel={
              formData.channel === 'sms' || formData.channel === 'whatsapp'
                ? 'sms'
                : formData.channel === 'voice'
                ? 'voice'
                : formData.channel === 'mail'
                ? 'mail'
                : 'email'
            }
            sampleOnly
            onGateChange={setComplianceGate}
          />
        </div>
      )}

      {/* Two-column layout — wrapped in a disabled fieldset when the engagement
          is in a terminal state (sent/cancelled/completed) so all inputs become
          non-editable. Users must Copy to make a new editable draft. */}
      <fieldset
        disabled={(['sent', 'cancelled', 'completed'] as string[]).includes(formData.status as string)}
        className="contents disabled:opacity-90 disabled:[&_*]:cursor-not-allowed"
      >
      <div className="flex-1 overflow-hidden flex">
        {/* Left column — 65% */}
        <div className="w-[65%] overflow-y-auto p-6 border-r border-gray-200 dark:border-gray-700">
          {isMessagingChannel ? (
            <BulkMessageComposer
              ref={composerRef}
              // On edit: prefer the saved engagement's group_id; otherwise fall back to URL-provided group.
              groupId={(() => {
                const eng = engagements.find(e => e.id === editingId);
                return eng?.group_id || initialGroupId;
              })()}
              // Re-mount the composer when switching between engagements so initial* props re-apply.
              key={editingId || 'new'}
              initialAssetIds={(() => {
                const eng = engagements.find(e => e.id === editingId);
                return eng?.asset_ids || [];
              })()}
              channel={formData.channel === 'voice' ? 'voice' as any : formData.channel as any}
              hideSendButton={true}
              initialMessage={(() => {
                // Load saved content_template back into composer
                const eng = engagements.find(e => e.id === editingId);
                if (!eng?.content_template) return undefined;
                const ct = eng.content_template;
                if (ct.startsWith('Subject:')) {
                  const parts = ct.split('\n\n');
                  return parts.slice(1).join('\n\n');
                }
                if (['Draft engagement', 'Survey engagement', 'Chatflow engagement'].includes(ct)) return undefined;
                return ct;
              })()}
              initialSubject={(() => {
                const eng = engagements.find(e => e.id === editingId);
                // Prefer the dedicated email_subject column when present.
                if (eng?.email_subject) return eng.email_subject;
                if (!eng?.content_template?.startsWith('Subject:')) return undefined;
                return eng.content_template.split('\n\n')[0].replace('Subject: ', '');
              })()}
              onSendSuccess={(taskId) => {
                console.log('Engagement sent', taskId);
                setView('list');
                fetchEngagements();
              }}
              onSendError={(error) => {
                console.error('Engagement send failed', error);
              }}
              onContentChange={(c) => {
                setComposerAssetIds(c.assetIds || []);
                setComposerTemplateId(c.templateId ?? null);
                setComposerGroupId(c.groupId || '');
              }}
            />
          ) : formData.channel === 'social' ? (
            <SocialMediaComposer
              onSendSuccess={(result) => {
                console.log('Social post sent', result);
                setView('list');
                fetchEngagements();
              }}
              onSendError={(error) => {
                console.error('Social post failed', error);
              }}
            />
          ) : formData.channel === 'survey' ? (
            <div className="space-y-5">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-amber-500" />
                Survey Engagement
              </h3>

              {/* Step 1: Select Survey */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">1. Select Survey</label>
                <select
                  value={formData.survey_id}
                  onChange={e => updateForm({ survey_id: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                >
                  <option value="">-- Choose a survey --</option>
                  {surveys.map(s => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                {surveys.length === 0 && (
                  <p className="text-xs text-gray-400 italic mt-1">No surveys found. Create one in Surveys & Polls first.</p>
                )}
              </div>

              {/* Step 2: Choose Delivery Channel */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">2. Delivery Channel</label>
                <p className="text-xs text-gray-500 mb-3">How should the survey be delivered to members?</p>
                <div className="space-y-2">
                  {[
                    { key: 'sms', label: 'SMS — Send survey link via text message', icon: <MessageSquare className="w-4 h-4" /> },
                    { key: 'email', label: 'Email — Send survey link via email', icon: <Mail className="w-4 h-4" /> },
                    { key: 'whatsapp', label: 'WhatsApp — Send survey link via WhatsApp', icon: <Phone className="w-4 h-4" /> },
                  ].map(opt => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => updateForm({ description: `survey_delivery:${opt.key}` })}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-sm text-left transition-colors ${
                        formData.description === `survey_delivery:${opt.key}`
                          ? 'border-v2accent bg-v2accent text-white'
                          : 'border-v2border text-ink-500 hover:text-ink'
                      }`}
                    >
                      {opt.icon}
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3: Customize Message */}
              {formData.survey_id && formData.description?.startsWith('survey_delivery:') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    3. Invitation Message
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {t('templateVariables.survey.invitationHelp', 'Members receive this with their own survey link.')}
                  </p>
                  <textarea
                    ref={surveyMessageRef}
                    rows={3}
                    value={surveyMessage}
                    onChange={(e) => setSurveyMessage(e.target.value)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <VariablePicker
                      appliesTo="messages"
                      onInsert={(key) => setSurveyMessage((m) => insertToken(surveyMessageRef.current, m, key))}
                    />
                    <TemplatePreviewButton appliesTo="messages" body={surveyMessage} />
                  </div>
                </div>
              )}

              {/* Info */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  When sent, members receive the invitation message with a unique survey link via the selected channel.
                  Responses are collected in Surveys & Polls and linked to this engagement.
                </p>
              </div>
            </div>
          ) : formData.channel === 'chatflow' ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <GitBranch className="w-5 h-5 text-teal-500" />
                Select Chatflow
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Choose an existing chatflow to power this engagement.
              </p>
              <select
                value={formData.chatflow_id}
                onChange={e => updateForm({ chatflow_id: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
              >
                <option value="">-- Select a chatflow --</option>
                {chatflows.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              {chatflows.length === 0 && (
                <p className="text-xs text-gray-400 italic">No chatflows found. Create one in the Chatflows section first.</p>
              )}
            </div>
          ) : formData.channel === 'mail' ? (
            <BulkMailCampaignPage />
          ) : null}
        </div>

        {/* Right column — 35% settings panel */}
        <div className="w-[35%] overflow-y-auto p-6 bg-white dark:bg-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider mb-5">
            Engagement Settings
          </h3>

          <div className="space-y-5">
            {/* Name */}
            <div>
              <label htmlFor="engagement-name-input" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="engagement-name-input"
                type="text"
                required
                aria-required="true"
                aria-invalid={!formData.name?.trim() ? 'true' : 'false'}
                value={formData.name}
                onChange={e => updateForm({ name: e.target.value })}
                placeholder="e.g. March Outreach Campaign"
                className={`w-full border rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  !formData.name?.trim()
                    ? 'border-red-300 dark:border-red-700'
                    : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {!formData.name?.trim() && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Required — give this engagement a clear name before saving.
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={e => updateForm({ description: e.target.value })}
                rows={3}
                placeholder="Brief description of this engagement..."
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            {/* Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1.5">
                <Globe className="w-4 h-4 text-blue-500" />
                Language
              </label>
              <select
                value={formData.language}
                onChange={e => updateForm({ language: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {LANGUAGES.map(lang => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <div className="flex gap-2">
                {(['draft', 'active'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => updateForm({ status: s })}
                    className={`flex-1 px-3 py-2 text-sm rounded-lg border font-medium transition-colors ${
                      formData.status === s
                        ? s === 'draft'
                          ? 'border-gray-400 bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                          : 'border-v2accent bg-v2accent text-white'
                        : 'border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {s === 'draft' ? 'Draft' : 'Active'}
                  </button>
                ))}
              </div>
            </div>

            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Schedule */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-yellow-500" />
                  Schedule for later
                </label>
                <button
                  onClick={() => updateForm({ schedule_enabled: !formData.schedule_enabled })}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    formData.schedule_enabled ? 'bg-v2accent' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      formData.schedule_enabled ? 'translate-x-5' : ''
                    }`}
                  />
                </button>
              </div>
              {formData.schedule_enabled && (
                <input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={e => updateForm({ scheduled_at: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              )}
              {/* Phase 5c — ML send-timing recommendation. Shown regardless
                  of schedule_enabled so users discover it; clicking Apply
                  turns scheduling ON and fills the time. */}
              <div className="mt-2">
                <OptimalSendTimeBadge
                  language={formData.language ?? undefined}
                  baseDate={formData.scheduled_at ? new Date(formData.scheduled_at) : undefined}
                  onSelectTime={(iso) => updateForm({ schedule_enabled: true, scheduled_at: iso })}
                />
              </div>
            </div>

            {/* Response settings */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-indigo-500" />
                  Wait for response
                </label>
                <button
                  onClick={() => updateForm({ wait_for_response: !formData.wait_for_response })}
                  className={`relative w-10 h-5 rounded-full transition-colors ${
                    formData.wait_for_response ? 'bg-v2accent' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      formData.wait_for_response ? 'translate-x-5' : ''
                    }`}
                  />
                </button>
              </div>
              {formData.wait_for_response && (
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Timeout (hours)</label>
                  <input
                    type="number"
                    min={1}
                    max={168}
                    value={formData.timeout_hours}
                    onChange={e => updateForm({ timeout_hours: parseInt(e.target.value) || 24 })}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
            </div>

            <hr className="border-gray-200 dark:border-gray-700" />

            {/* Info card */}
            <div className="bg-v2accent-soft border border-v2border rounded-lg p-4">
              <h4 className="text-sm font-medium text-v2accent-ink mb-2 flex items-center gap-1.5">
                <CheckCircle className="w-4 h-4" />
                Engagement Lifecycle
              </h4>
              <ul className="text-xs text-v2accent-ink space-y-1.5 list-disc list-inside">
                <li><strong>Draft</strong> — saved but not sent. Edit anytime.</li>
                <li><strong>Scheduled</strong> — queued for future delivery at the chosen date/time.</li>
                <li><strong>Active / Sent</strong> — delivered to recipients. Responses tracked automatically.</li>
                <li>Use <strong>Wait for response</strong> to pause the workflow until recipients reply or the timeout elapses.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      </fieldset>

      {/* Individual Send Modal */}
      {showIndividualSend && editingId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[95vh] overflow-y-auto">
            {!individualSendGroupId ? (
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Select Group for Individual Send</h2>
                  <button onClick={() => { setShowIndividualSend(false); setIndividualSendGroupId(''); }} className="text-gray-400 hover:text-gray-600">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mb-4">Choose a member group to send this engagement to individually.</p>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {groups.map(g => (
                    <button
                      key={g.id}
                      onClick={() => setIndividualSendGroupId(g.id)}
                      className="w-full text-left px-4 py-3 border rounded-lg hover:bg-paper-2 transition-colors flex items-center justify-between"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{g.name}</div>
                        <div className="text-xs text-gray-500">{g.member_count} members</div>
                      </div>
                      <Users className="w-4 h-4 text-gray-400" />
                    </button>
                  ))}
                  {groups.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-8">No groups found. Create a group first.</p>
                  )}
                </div>
              </div>
            ) : (
              <EngagementMemberSendInline
                engagementId={editingId!}
                groupId={individualSendGroupId}
                onClose={() => { setShowIndividualSend(false); setIndividualSendGroupId(''); }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkCommunicationsPage;
